import { productSessionCookies } from "@ilabel/product-config";
import { productApiPath } from "./product-runtime.js";
export class ApiError extends Error {
    status;
    code;
    constructor(message, status, code) {
        super(message);
        this.status = status;
        this.code = code;
        this.name = "ApiError";
    }
}
const API_TIMEOUT_MS = 15_000;
const isApiEnvelope = (payload) => typeof payload === "object" &&
    payload !== null &&
    typeof payload.success === "boolean";
const isApiErrorPayload = (payload) => typeof payload === "object" &&
    payload !== null &&
    typeof payload.message === "string";
function cookie(name) {
    return (document.cookie
        .split(";")
        .map((part) => part.trim())
        .find((part) => part.startsWith(`${name}=`))
        ?.slice(name.length + 1) ?? "");
}
export function createProductApiClient(config) {
    const cookieNames = productSessionCookies(config.code);
    return async function request(path, init = {}) {
        const headers = new Headers(init.headers);
        if (init.body && !headers.has("content-type"))
            headers.set("content-type", "application/json");
        const csrf = cookie(cookieNames.csrf);
        if (csrf)
            headers.set("x-csrf-token", decodeURIComponent(csrf));
        const controller = new AbortController();
        let timedOut = false;
        const timeoutId = globalThis.setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, API_TIMEOUT_MS);
        const forwardAbort = () => controller.abort(init.signal?.reason);
        if (init.signal?.aborted)
            forwardAbort();
        else
            init.signal?.addEventListener("abort", forwardAbort, { once: true });
        let response;
        try {
            response = await fetch(productApiPath(config, path), {
                ...init,
                headers,
                credentials: "include",
                signal: controller.signal,
            });
        }
        catch {
            if (timedOut)
                throw new ApiError("请求超时，请稍后重试", 0, "REQUEST_TIMEOUT");
            throw new ApiError("网络异常，请稍后重试", 0, "NETWORK_ERROR");
        }
        finally {
            globalThis.clearTimeout(timeoutId);
            init.signal?.removeEventListener("abort", forwardAbort);
        }
        if (response.status >= 500) {
            // Only these public codes may replace the generic server error.
            // Never display an arbitrary upstream error message or stack to the user.
            if (response.status === 503) {
                const payload = await response.json().catch(() => null);
                const code = payload && typeof payload === "object" && "error" in payload
                    ? payload.error : undefined;
                if (code === "OPERATION_PIPELINE_DISPATCH_FAILED")
                    throw new ApiError("标注已提交，后台处理任务入队失败。请返回工作台查看处理结果。", 503, code);
                if (code === "OPERATION_PIPELINE_DISPATCH_UNCONFIRMED")
                    throw new ApiError("标注已提交，后台处理状态暂未确认。请返回工作台查看处理结果。", 503, code);
                if (code === "SMS_NOT_CONFIGURED" || code === "SMS_SERVICE_UNAVAILABLE")
                    throw new ApiError(code === "SMS_NOT_CONFIGURED"
                        ? "短信服务未配置，请联系管理员"
                        : "验证码服务暂不可用，请稍后重试", 503, code);
            }
            throw new ApiError("服务暂不可用，请稍后重试", response.status, "SERVICE_UNAVAILABLE");
        }
        let responseText;
        try {
            responseText = await response.text();
        }
        catch {
            throw new ApiError("网络异常，请稍后重试", 0, "NETWORK_ERROR");
        }
        if (!responseText.trim())
            throw new ApiError("系统响应异常，请稍后重试", response.status, "INVALID_RESPONSE");
        let payload;
        try {
            payload = JSON.parse(responseText);
        }
        catch {
            throw new ApiError("系统响应异常，请稍后重试", response.status, "INVALID_RESPONSE");
        }
        if (!response.ok && isApiErrorPayload(payload))
            throw new ApiError(payload.message, response.status, typeof payload.error === "string" ? payload.error : undefined);
        if (!isApiEnvelope(payload))
            throw new ApiError("系统响应异常，请稍后重试", response.status, "INVALID_RESPONSE");
        const message = typeof payload.message === "string" && payload.message.trim()
            ? payload.message
            : `HTTP ${response.status}`;
        const errorCode = typeof payload.error === "string" ? payload.error : undefined;
        if (!response.ok || !payload.success)
            throw new ApiError(message, response.status, errorCode);
        return payload.data;
    };
}
//# sourceMappingURL=product-api-client.js.map